import { useCallback, useMemo, useState } from "react";
import type { Match, Player } from "../types";
import { IS_E2E_TEST_MODE } from "../../../lib/loggerApi";
import {
  EXTRA_FIRST_HALF_END_SECONDS,
  EXTRA_HALF_MINUTES,
  REGULATION_FIRST_HALF_SECONDS,
  REGULATION_SECOND_HALF_SECONDS,
  resolvePeriodStartSeconds,
  type PeriodPhase,
} from "./usePeriodManager";
import { formatSecondsAsClock } from "../lib/clockHelpers";

// EPIC-08 Phase 1 (PMA-COCK-089): kickoff (Pending -> Live_First_Half)
// requires exactly 11 starters per side. This is a client-side UX
// pre-check only — the server remains authoritative (both status routes
// reject the transition with 400 "Kickoff requires exactly 11 starters
// per side (home: N, away: M)" via the shared kickoff_lineup_error()
// helper in app/utils/match_status.py). Kept in sync with the backend's
// KICKOFF_STARTERS_REQUIRED constant.
const KICKOFF_STARTERS_REQUIRED = 11;

// Mirrors the defensive is_starter predicate already used by
// useOnFieldRoster.ts (getInitialOnField) — API data isn't guaranteed to
// hand back a clean boolean.
const isStarterFlag = (player: Player): boolean =>
  player.is_starter === true ||
  (player.is_starter as unknown) === "true" ||
  (player.is_starter as unknown) === 1;

const countStarters = (players: Player[] | undefined): number =>
  (players || []).filter(isStarterFlag).length;

interface UseTransitionGuardsParams {
  statusOverride?: Match["status"];
  currentPhase: PeriodPhase;
  globalTimeSeconds: number;
  match: Match | null;
  t: (...args: any[]) => any;
}

export interface UseTransitionGuardsResult {
  currentStatusNormalized: Match["status"];
  canHalftime: boolean;
  canSecondHalf: boolean;
  canFulltime: boolean;
  transitionGuardMessage: string;
  transitionError: string | null;
  hasFirstHalfMinimum: boolean;
  hasSecondHalfMinimum: boolean;
  hasExtraFirstHalfMinimum: boolean;
  hasExtraSecondHalfMinimum: boolean;
  minimumFirstHalfReason: string;
  minimumSecondHalfReason: string;
  minimumExtraFirstHalfReason: string;
  minimumExtraSecondHalfReason: string;
  hasKickoffLineup: boolean;
  kickoffLineupReason: string;
  guardTransition: (target: Match["status"], fn?: () => void) => void;
  isTransitionAllowed: (
    target: Match["status"],
    currentOverride?: Match["status"],
  ) => boolean;
}

export const useTransitionGuards = ({
  statusOverride,
  currentPhase,
  globalTimeSeconds,
  match,
  t,
}: UseTransitionGuardsParams): UseTransitionGuardsResult => {
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const normalizeStatus = useCallback(
    (status?: Match["status"]): Match["status"] => {
      if (status === "Live") return "Live_First_Half";
      return status || "Pending";
    },
    [],
  );

  const isTransitionAllowed = useCallback(
    (target: Match["status"], currentOverride?: Match["status"]): boolean => {
      const current = normalizeStatus(currentOverride ?? statusOverride);
      const allowed: Record<Match["status"], Match["status"][]> = {
        Pending: ["Live_First_Half"],
        Live_First_Half: ["Halftime", "Fulltime"],
        Halftime: ["Live_Second_Half"],
        Live_Second_Half: ["Fulltime"],
        Live: ["Halftime"],
        Fulltime: ["Live_Extra_First", "Penalties", "Completed"],
        Abandoned: [],
        Live_Extra_First: ["Extra_Halftime"],
        Extra_Halftime: ["Live_Extra_Second"],
        Live_Extra_Second: ["Penalties", "Completed"],
        Penalties: ["Completed"],
        Completed: [],
        Scheduled: ["Live_First_Half"],
      };
      const allowedTargets = allowed[current] || [];
      return allowedTargets.includes(target);
    },
    [normalizeStatus, statusOverride],
  );

  const bypassMinimums =
    IS_E2E_TEST_MODE &&
    !(
      typeof window !== "undefined" &&
      (window as any).__PROMATCH_E2E_ENFORCE_MINIMUMS__
    );

  const getPeriodStartSeconds = useCallback(
    (period: number) => {
      if (period === 1) return 0;

      const canonicalStart =
        period === 2
          ? REGULATION_FIRST_HALF_SECONDS
          : period === 3
            ? REGULATION_SECOND_HALF_SECONDS
            : period === 4
              ? EXTRA_FIRST_HALF_END_SECONDS
              : globalTimeSeconds;

      const raw =
        match?.period_timestamps?.[String(period)]?.global_start_seconds;
      return resolvePeriodStartSeconds(raw, canonicalStart, globalTimeSeconds);
    },
    [globalTimeSeconds, match],
  );

  const firstHalfElapsed = Math.max(
    0,
    globalTimeSeconds - getPeriodStartSeconds(1),
  );
  const secondHalfElapsed = Math.max(
    0,
    globalTimeSeconds - getPeriodStartSeconds(2),
  );
  const extraFirstElapsed = Math.max(
    0,
    globalTimeSeconds - getPeriodStartSeconds(3),
  );
  const extraSecondElapsed = Math.max(
    0,
    globalTimeSeconds - getPeriodStartSeconds(4),
  );

  const hasFirstHalfMinimum =
    bypassMinimums || firstHalfElapsed >= REGULATION_FIRST_HALF_SECONDS;
  const hasSecondHalfMinimum =
    bypassMinimums || secondHalfElapsed >= REGULATION_FIRST_HALF_SECONDS;
  const hasExtraFirstHalfMinimum =
    bypassMinimums || extraFirstElapsed >= EXTRA_HALF_MINUTES * 60;
  const hasExtraSecondHalfMinimum =
    bypassMinimums || extraSecondElapsed >= EXTRA_HALF_MINUTES * 60;

  // Kickoff lineup pre-check (PMA-COCK-089). Deliberately NOT gated by
  // bypassMinimums — that flag exists to skip realistic elapsed-clock
  // waits in E2E, an unrelated concern from squad completeness. Every
  // existing cockpit E2E fixture already has full 11-a-side lineups
  // (matches required exactly 11 starters to even be created before
  // EPIC-08 Phase 1), so this reflects true state in both E2E and prod.
  const homeStarterCount = countStarters(match?.home_team?.players);
  const awayStarterCount = countStarters(match?.away_team?.players);
  const hasKickoffLineup =
    homeStarterCount === KICKOFF_STARTERS_REQUIRED &&
    awayStarterCount === KICKOFF_STARTERS_REQUIRED;

  const minimumFirstHalfReason = t(
    "transitionMinimumFirstHalf",
    "Need at least 45:00 of global time from 1st half start (current {{clock}}).",
    { clock: formatSecondsAsClock(firstHalfElapsed) },
  );
  const minimumSecondHalfReason = t(
    "transitionMinimumSecondHalf",
    "Need at least 45:00 from 2nd half start (current {{clock}}).",
    { clock: formatSecondsAsClock(secondHalfElapsed) },
  );
  const minimumExtraFirstHalfReason = t(
    "transitionMinimumExtraFirstHalf",
    "Need at least 15:00 from ET 1st half start (current {{clock}}).",
    { clock: formatSecondsAsClock(extraFirstElapsed) },
  );
  const minimumExtraSecondHalfReason = t(
    "transitionMinimumExtraSecondHalf",
    "Need at least 15:00 from ET 2nd half start (current {{clock}}).",
    { clock: formatSecondsAsClock(extraSecondElapsed) },
  );
  const kickoffLineupReason = t(
    "transitionKickoffLineupRequired",
    "Kickoff requires 11 starters per side (home: {{home}}, away: {{away}}). Open the lineup panel above to add starters.",
    { home: homeStarterCount, away: awayStarterCount },
  );

  const guardTransition = useCallback(
    (target: Match["status"], fn?: () => void) => {
      if (!fn) return;
      const current = normalizeStatus(statusOverride);
      const phaseDerivedStatus =
        currentPhase === "NOT_STARTED"
          ? "Pending"
          : currentPhase === "FIRST_HALF"
            ? "Live_First_Half"
            : currentPhase === "HALFTIME"
              ? "Halftime"
              : currentPhase === "SECOND_HALF"
                ? "Live_Second_Half"
                : currentPhase === "FULLTIME"
                  ? "Fulltime"
                  : currentPhase === "FIRST_HALF_EXTRA_TIME"
                    ? "Live_Extra_First"
                    : currentPhase === "EXTRA_HALFTIME"
                      ? "Extra_Halftime"
                      : currentPhase === "SECOND_HALF_EXTRA_TIME"
                        ? "Live_Extra_Second"
                        : currentPhase === "PENALTIES"
                          ? "Penalties"
                          : currentPhase === "COMPLETED"
                            ? "Completed"
                            : undefined;
      const allowedStatus = phaseDerivedStatus ?? current;

      if (target === "Live_First_Half" && !hasKickoffLineup) {
        setTransitionError(kickoffLineupReason);
        return;
      }

      if (
        target === "Halftime" &&
        currentPhase === "FIRST_HALF" &&
        !hasFirstHalfMinimum
      ) {
        setTransitionError(minimumFirstHalfReason);
        return;
      }

      // Direct first-half → Fulltime is legal per the backend graph
      // (LIVE_FIRST_HALF: {HALFTIME, ABANDONED, FULLTIME}). Enforce the same
      // first-half minimum as the Halftime transition (≥ 45:00 elapsed).
      if (
        target === "Fulltime" &&
        currentPhase === "FIRST_HALF" &&
        !hasFirstHalfMinimum
      ) {
        setTransitionError(minimumFirstHalfReason);
        return;
      }

      if (
        target === "Fulltime" &&
        currentPhase === "SECOND_HALF" &&
        !hasSecondHalfMinimum
      ) {
        setTransitionError(minimumSecondHalfReason);
        return;
      }

      if (
        target === "Extra_Halftime" &&
        currentPhase === "FIRST_HALF_EXTRA_TIME" &&
        !hasExtraFirstHalfMinimum
      ) {
        setTransitionError(minimumExtraFirstHalfReason);
        return;
      }

      // PMA-COCK-200 (UC2, locked decision 2, 2026-09-16): entering
      // Penalties is deliberately EXEMPT from this minimum — a referee can
      // end a period early and the operator must be able to follow into
      // the shootout. This block used to also match `target === "Penalties"`
      // here, which meant the fulltime-options Penalties button could
      // render enabled (see `penaltiesTransitionDisabled` in
      // useCockpitTransitionState.ts, which never folds in
      // hasExtraSecondHalfMinimum) while a click on it was still silently
      // swallowed right here. Fulltime and Completed — the actual
      // period-end controls in this same branch ("End Match" from
      // SECOND_HALF_EXTRA_TIME) — keep the guard unchanged (criterion 5).
      if (
        (target === "Fulltime" || target === "Completed") &&
        currentPhase === "SECOND_HALF_EXTRA_TIME" &&
        !hasExtraSecondHalfMinimum
      ) {
        setTransitionError(minimumExtraSecondHalfReason);
        return;
      }

      if (!isTransitionAllowed(target, allowedStatus)) {
        setTransitionError(
          `Transition not allowed: ${current} → ${target}. Follow Pending → Live_First_Half → Halftime → Live_Second_Half → Fulltime.`,
        );
        return;
      }

      setTransitionError(null);
      fn();
    },
    [
      currentPhase,
      hasFirstHalfMinimum,
      hasSecondHalfMinimum,
      hasExtraFirstHalfMinimum,
      hasExtraSecondHalfMinimum,
      hasKickoffLineup,
      isTransitionAllowed,
      kickoffLineupReason,
      minimumFirstHalfReason,
      minimumSecondHalfReason,
      minimumExtraFirstHalfReason,
      minimumExtraSecondHalfReason,
      normalizeStatus,
      statusOverride,
    ],
  );

  const currentStatusNormalized = normalizeStatus(statusOverride);
  const canHalftime = isTransitionAllowed("Halftime");
  const canSecondHalf = isTransitionAllowed("Live_Second_Half");
  const canFulltime = isTransitionAllowed("Fulltime");

  const transitionGuardMessage = useMemo(
    () =>
      t(
        "transitionGuardMessage",
        "Follow order: Pending → Live_First_Half → Halftime → Live_Second_Half → Fulltime (current: {{status}}).",
        { status: currentStatusNormalized },
      ),
    [currentStatusNormalized, t],
  );

  return {
    currentStatusNormalized,
    canHalftime,
    canSecondHalf,
    canFulltime,
    transitionGuardMessage,
    transitionError,
    hasFirstHalfMinimum,
    hasSecondHalfMinimum,
    hasExtraFirstHalfMinimum,
    hasExtraSecondHalfMinimum,
    minimumFirstHalfReason,
    minimumSecondHalfReason,
    minimumExtraFirstHalfReason,
    minimumExtraSecondHalfReason,
    hasKickoffLineup,
    kickoffLineupReason,
    guardTransition,
    isTransitionAllowed,
  };
};
